import { useAuthSession } from "@bitsentry-ce/components/services/hooks";
import { Navigate, useLocation } from "react-router-dom";
import { Skeleton } from "../ui/skeleton";

interface RoleRouteProps {
  children: React.ReactNode;
  allowedRoles: number[];
}

/**
 * Route guard that checks authentication and role-based access.
 *
 * Usage:
 *   <RoleRoute allowedRoles={[1]}>         // Admin only
 *   <RoleRoute allowedRoles={[1, 3]}>      // Admin + Security Admin
 *   <RoleRoute allowedRoles={[1, 3, 4]}>   // Admin + Security Admin + Security Analyst
 *
 * Role IDs: admin=1, user=2, security_admin=3, security_analyst=4, security_viewer=5
 */
export const RoleRoute: React.FC<RoleRouteProps> = ({
  children,
  allowedRoles,
}) => {
  const { isAuthenticated, isLoading, user } = useAuthSession();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="space-y-4 w-[400px]">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const userRoleId = user?.role?.id;
  if (userRoleId === undefined || !allowedRoles.includes(userRoleId)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
